import type { TripView } from "./api.ts";
import { tripDates, tripDatesShort } from "./format.ts";
import { TripDates } from "./TripMenu.tsx";
import { ButtonLink } from "./ui.tsx";
import type { ViewingInfo } from "./viewing.ts";

// Where someone without Tripmo starts their own (features/directories.md, "tripmo.me/connect"), saying which view sent
// them: a view sends no referrer, so tripmo.me's links pass this utm_source on to the app and Numbers credits the
// sample trip or a shared trip with the sign-ups it brings (features/numbers.md, "A trip's view sends people too").
export const connectUrl = (from: "sample" | "shared") => `https://tripmo.me/connect?utm_source=${from}`;

// The bar on every view of a shared trip (features/sharing.md, "What a viewer sees"): it's view only, who shared it
// (the owner's first name, when names are shared), or, on Tripmo's own sample trip (features/sample-trip.md, "How
// people reach it"), that it's a sample and what it is; and the way on: for someone without an account, planning their
// own with Tripmo; signed in and in no trip, starting one; in trips of their own, to them; for one of the trip's own
// members looking at it as a viewer would, back to the trip. On a computer a band over the trip's bar; on a phone one
// slim strip across the top of the screen, above the trip's name, never over the map (features/sharing.md, "What a
// viewer gets"; DESIGN.md "On a phone, the map comes first"): the words on one line, the way on a small button.
export function ViewBar({ viewing, phone }: { viewing: ViewingInfo; phone: boolean }) {
  const { viewer, sharedBy, sample } = viewing;
  const way = viewer.member_of
    ? {
        href: `/t/${encodeURIComponent(viewer.member_of)}?settings=1`,
        text: "Back to the trip",
        short: "Back to the trip",
      }
    : !viewer.signed_in
      ? { href: connectUrl(sample ? "sample" : "shared"), text: "Plan your own trip with Tripmo", short: "Plan yours" }
      : viewer.in_trips
        ? { href: "/", text: "Your trips", short: "Your trips" }
        : { href: "/", text: "Start a trip", short: "Start a trip" };
  const variant = viewer.member_of ? "secondary" : "primary";
  const by = sample
    ? plannedBy(sample.people)
    : viewer.member_of
      ? "As viewers see it"
      : sharedBy
        ? `Shared by ${sharedBy}`
        : "Shared with you";
  const label = sample ? "A sample trip" : "View only";
  if (phone)
    return (
      <div className="tm-view-strip" role="note" aria-label={label}>
        <span className="tm-view-strip-text">
          <span className="callout">{label}</span>
          {/* The sample says only that it is one: who planned it doesn't fit on one line beside its button. */}
          {!sample && (
            <>
              <span className="tm-muted" aria-hidden="true">
                {" · "}
              </span>
              <span className="tm-view-bar-by">{by}</span>
            </>
          )}
        </span>
        {/* The strip's whole height is the button's tap target; the button itself is small. */}
        <a className="tm-view-strip-way" href={way.href}>
          <span className={`sv-btn sv-btn--${variant} sv-btn--sm tm-view-strip-button`}>{way.short}</span>
        </a>
      </div>
    );
  return (
    <div className="tm-view-bar" role="note" aria-label={label}>
      <span className="tm-view-bar-text">
        <span className="callout">{label}</span>
        {by && (
          <>
            <span className="tm-muted tm-view-bar-dot" aria-hidden="true">
              ·
            </span>
            <span className="tm-view-bar-by">{by}</span>
          </>
        )}
      </span>
      <ButtonLink href={way.href} variant={variant} size="sm" className="tm-view-bar-way">
        {way.text}
      </ButtonLink>
    </div>
  );
}

// What the sample is, in a few words: who plans it, each through their own AI (the point of Tripmo). Nothing for a
// count without a word (a sample of one, or more than twelve).
const COUNTS = ["", "", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
export function plannedBy(people: number): string | null {
  const count = COUNTS[people];
  return count ? `Planned by ${count} friends with their own AIs` : null;
}

// A shared trip's name and dates, as the trip's bar shows a trip's menu but no menu: a viewer has nowhere else in it to
// go (features/sharing.md: no Trip settings, no other trips).
export function StaticTripName({ trip, className }: { trip: TripView; className: string }) {
  const dates = tripDates(trip.dates);
  return (
    <div className="tm-trip-menu-wrap">
      <h1 className={`${className} is-static`} aria-label={dates ? `${trip.title}, ${dates}` : trip.title}>
        {dates && <TripDates full={dates} short={tripDatesShort(trip.dates)} />}
        <span className="tm-trip-title">
          <span className="title">{trip.title}</span>
        </span>
      </h1>
    </div>
  );
}
